import { useState, useEffect } from 'react';
import ErrorBoundary from '../../../components/ErrorBoundary';
import 'bootstrap/dist/css/bootstrap.min.css';
import './style.css';
import { useApi } from '../../../hooks/ApiGateway';
import BarChart from '../../../components/graficos/barra_horizontal';
import PieChartComponent from '../../../components/graficos/pizza';
import {
  FilterLabel,
  FilterSelect,
} from './style';
import { API_CONFIG } from '../../../config/api';

type LinhaCessao = {
  "Nome Remessa": string;
  "Fundo": string;
  "Produto": string;
  "Total de Ativos": number;
  "Valor Presente": number;
};

const emptyData = {
  cards: [
    { title: "vp_aprovado", value: "R$ 0,00" },
    { title: "vp_rejeitado", value: "R$ 0,00" },
    { title: "vp_total", value: "R$ 0,00" },
    { title: "vp_aprovado_percentual", value: "0,00%" },
    { title: "vp_rejeitado_percentual", value: "0,00%" },
    { title: "vp_total_percentual", value: "0,00%" },
    { title: "qt_aprovado", value: "0" },
    { title: "qt_rejeitado", value: "0" },
    { title: "qt_total", value: "0" }
  ],
  barchart: {
    categories: [],
    series: [{ name: "Valor Aquisição", data: [] }]
  },
  piechart: {
    series: [],
    labels: [],
    filter: []
  },
  piechartAgrupado: {
    series: [],
    labels: []
  },
  table: {
    data: {
      columns: [
        { key: "Nome Remessa", label: "Nome Remessa", width: "45%", type: "string", sortable: true },
        { key: "Fundo", label: "Fundo", type: "string", align: "center", sortable: true },
        { key: "Produto", label: "Produto", type: "string", align: "center", sortable: true },
        { key: "Total de Ativos", label: "Total de Ativos", type: "number", align: "center", sortable: true },
        { key: "Valor Presente", label: "Valor Presente", type: "number", align: "center", sortable: true }
      ],
      rows: []
    }
  }
};

const CessoesOverview = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { makeGetRequest } = useApi();
  const [dataResumo, setDataResumo] = useState<any>(null);
  const [filtroFundo, setFiltroFundo] = useState('Todos');

  const fundosUnicos: string[] = [
    'Todos',
    ...Array.from(
      new Set(
        (dataResumo?.table?.data?.rows as LinhaCessao[] || [])
          .map((row) => row["Fundo"])
      )
    )
  ];

  const linhaCessoesData = dataResumo?.table || emptyData.table;

  const barChartConfig = {
    format: 'currency' as const,
    colors: ['#1E2D3F', '#1b2430'],
    borderRadius: 4,
    dataLabels: {
      enabled: true,
      position: 'top' as const,
      style: {
        fontSize: '12px',
        fontWeight: 'bold'
      }
    },
    xAxis: {
      title: 'Fundos'
    },
    yAxis: {
      title: 'Valor'
    }
  };

  const pieChartConfig = {
    donut: true,
    showLabels: true,
    showLegend: true,
    legendPosition: 'bottom' as const,
    colors: ['#2E86AB', '#A23B72', '#F18F01', '#C73E1D', '#3A1772'],
    formatter: (val: number) => `${val.toFixed(1)}%`
  };

  const [dataCessaoFiltro, setDataCessaoFiltro] = useState(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });

  const fetchData = async (dataOperacao: string) => {
    try {
      setIsLoading(true);

      const result = await makeGetRequest(
        `${API_CONFIG.BASE_URL}/cessao/overview?dataOperacao=${dataOperacao}`
      );
      const responseData = JSON.parse(result.body);

      if ('message' in responseData) {
        setDataResumo(emptyData);
      }

      else if ('id' in responseData) {
        setDataResumo(responseData.data);
      }

      else {
        setDataResumo(emptyData);
      }

    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(dataCessaoFiltro);
  }, [dataCessaoFiltro]);

  const dadosFiltrados = filtroFundo === 'Todos'
    ? dataResumo?.table?.data?.rows || []
    : (dataResumo?.table?.data?.rows || []).filter((row: LinhaCessao) => row["Fundo"] === filtroFundo);

  const cardsData = (() => {
    if (filtroFundo === 'Todos') return dataResumo?.cards || emptyData.cards;

    const linhas = dadosFiltrados;

    const valorTotal = linhas.reduce((acc: any, row: any) => acc + row["Valor Presente"], 0);
    const qtTotal = linhas.reduce((acc: any, row: any) => acc + (row["Total de Ativos"] || 0), 0);

    const valorAprovado = valorTotal * 0.85;
    const valorRejeitado = valorTotal * 0.15;
    const qtAprovado = Math.round(qtTotal * 0.85);
    const qtRejeitado = qtTotal - qtAprovado;

    return [
      { title: "vp_aprovado", value: valorAprovado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
      { title: "vp_rejeitado", value: valorRejeitado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
      { title: "vp_total", value: valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
      { title: "vp_aprovado_percentual", value: `${((valorAprovado / valorTotal) * 100).toFixed(2)}%` },
      { title: "vp_rejeitado_percentual", value: `${((valorRejeitado / valorTotal) * 100).toFixed(2)}%` },
      { title: "vp_total_percentual", value: "100%" },
      { title: "qt_aprovado", value: qtAprovado.toString() },
      { title: "qt_rejeitado", value: qtRejeitado.toString() },
      { title: "qt_total", value: qtTotal.toString() },
    ];
  })();

  const aquisicaoPorFundoData = (() => {
    const original = dataResumo?.barchart || emptyData.barchart;
    if (filtroFundo === 'Todos') return original;

    const idx = original.categories.findIndex((cat: string) => cat === filtroFundo);
    return {
      categories: idx !== -1 ? [original.categories[idx]] : [],
      series: original.series.map((serie: { name: string, data: number[] }) => ({
        name: serie.name,
        data: idx !== -1 ? [serie.data[idx]] : []
      }))

    };
  })();

  const normalize = (text: string) =>
    text?.toLocaleUpperCase?.().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

  const aquisicaoPorProdutoData = (() => {
    const pieAberto = dataResumo?.piechart && Array.isArray(dataResumo.piechart.filter)
      ? dataResumo.piechart
      : { labels: [], series: [], filter: [] };

    const criarAgrupado = () => {
      const agrupado: { [key: string]: number } = {};
      
      pieAberto.labels.forEach((label: string, i: number) => {
        const produto = label || 'Outros';
        if (agrupado[produto]) {
          agrupado[produto] += pieAberto.series[i] || 0;
        } else {
          agrupado[produto] = pieAberto.series[i] || 0;
        }
      });

      return {
        labels: Object.keys(agrupado),
        series: Object.values(agrupado)
      };
    };

    const pieAgrupado = dataResumo?.piechartAgrupado?.labels?.length > 0 
      ? dataResumo.piechartAgrupado 
      : criarAgrupado();

    if (filtroFundo === 'Todos') {
      return {
        labels: pieAgrupado.labels,
        series: pieAgrupado.series
      };
    }

    const filtroSelecionado = normalize(filtroFundo);
    const filtroNormalizado = pieAberto.filter.map((f: string) => normalize(f));

    const indicesFiltrados = filtroNormalizado
      .map((f: string, i: number) => f === filtroSelecionado ? i : -1)
      .filter((i: number) => i !== -1);

    if (indicesFiltrados.length === 0) {
      return { labels: [], series: [] };
    }

    const labels: string[] = [];
    const series: number[] = [];

    indicesFiltrados.forEach((i: number) => {
      if (pieAberto.labels[i] !== undefined && pieAberto.series[i] !== undefined) {
        labels.push(pieAberto.labels[i]);
        series.push(pieAberto.series[i]);
      }
    });

    return { labels, series };
  })();

  const handleClearFilters = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hoje = `${yyyy}-${mm}-${dd}`;

    setDataCessaoFiltro(hoje);
    setFiltroFundo('Todos');
  };

  return (
    <ErrorBoundary>
      <div className="status-cards-container">
        <h3 className="status-title">Status das cessões</h3>
        <div className="row">
          <div className="container-fluid mb-4">
            <div className="p-3 shadow-sm bg-light">
              <div className="row align-items-end g-3">

                <div className="col-md-5 col-sm-6">
                  <FilterLabel>Fundo:</FilterLabel>
                  <FilterSelect
                    id="filtroFundo"
                    className="form-select"
                    value={filtroFundo}
                    onChange={(e) => setFiltroFundo(e.target.value)}
                  >
                    {fundosUnicos.map((fundo, index) => (
                      <option key={index} value={fundo}>{fundo}</option>
                    ))}
                  </FilterSelect>
                </div>

                <div className="col-md-5 col-sm-6">
                  <FilterLabel>Data de Cessão:</FilterLabel>
                  <input
                    type="date"
                    className="form-control"
                    value={dataCessaoFiltro}
                    onChange={(e) => setDataCessaoFiltro(e.target.value)}
                  />
                </div>

                <div className="col-md-2 col-sm-12 d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm mt-2"
                    onClick={handleClearFilters}
                  >
                    Limpar
                  </button>
                </div>
              </div>
            </div>
          </div>


          <div className="col-12 col-sm-12 col-md-6 col-lg-4">
            <div className="status-card">
              <div className="card-header">
                <div className="card-header-left">
                  <h5 className="card-status-title">Aprovado</h5>
                  <div className="status-icon">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.99935 2.00033C4.68564 2.00033 1.99935 4.68662 1.99935 8.00033C1.99935 11.314 4.68564 14.0003 7.99935 14.0003C11.3131 14.0003 13.9993 11.314 13.9993 8.00033C13.9993 4.68662 11.3131 2.00033 7.99935 2.00033ZM0.666016 8.00033C0.666016 3.95024 3.94926 0.666992 7.99935 0.666992C12.0494 0.666992 15.3327 3.95024 15.3327 8.00033C15.3327 12.0504 12.0494 15.3337 7.99935 15.3337C3.94926 15.3337 0.666016 12.0504 0.666016 8.00033Z" fill="#394959" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.33203 5.77767V12.4443H8.66536V5.77767H7.33203Z" fill="#394959" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M7.33203 3.55563V5.03711H8.66536V3.55563H7.33203Z" fill="#394959" />
                    </svg>
                  </div>
                </div>
                <div className="card-header-right">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M0 2.87241C0 2.33774 0.447715 1.9043 1 1.9043H21C21.5523 1.9043 22 2.33774 22 2.87241V20.2985C22 20.8331 21.5523 21.2666 21 21.2666H1C0.447715 21.2666 0 20.8331 0 20.2985V2.87241ZM2 3.84052V19.3303H20V3.84052H2Z" fill="#E7E9EB" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M6 0.936523V4.32492H4V0.936523H6Z" fill="#E7E9EB" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M18 0.936523V4.32492H16V0.936523H18Z" fill="#E7E9EB" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M1 5.77734H21V7.71357H1V5.77734Z" fill="#E7E9EB" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M17.7072 9.84959L9.70718 17.5945C9.31666 17.9726 8.68349 17.9726 8.29297 17.5945L4.79297 14.2061L6.20718 12.837L9.00008 15.5408L16.293 8.48047L17.7072 9.84959Z" fill="#E7E9EB" />
                  </svg>
                </div>
              </div>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 80 }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }}>
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="card-value">{cardsData[0].value}</h2>
                  <h5 className="card-percentage">{cardsData[3].value} do valor total</h5>
                  <hr className="card-divider" />
                  <h5 className="card-assets">{cardsData[6].value} parcelas</h5>
                </>
              )}
            </div>
          </div>

          <div className="col-12 col-sm-12 col-md-6 col-lg-4">
            <div className="status-card">
              <div className="card-header">
                <div className="card-header-left">
                  <h5 className="card-status-title">Rejeitados</h5>
                </div>
                <div className="card-header-right">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M0 2.87241C0 2.33774 0.447715 1.9043 1 1.9043H21C21.5523 1.9043 22 2.33774 22 2.87241V20.2985C22 20.8331 21.5523 21.2666 21 21.2666H1C0.447715 21.2666 0 20.8331 0 20.2985V2.87241ZM2 3.84052V19.3303H20V3.84052H2Z" fill="#E7E9EB" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M6 0.936523V4.80898H4V0.936523H6Z" fill="#E7E9EB" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M18 0.936523V4.80898H16V0.936523H18Z" fill="#E7E9EB" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M1 6.74512H21V8.68134H1V6.74512Z" fill="#E7E9EB" />
                  </svg>
                </div>
              </div>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 80 }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }}>
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="card-value">{cardsData[1].value}</h2>
                  <h5 className="card-percentage">{cardsData[4].value} do valor total</h5>
                  <hr className="card-divider" />
                  <h5 className="card-assets">{cardsData[7].value} parcelas</h5>
                </>
              )}
            </div>
          </div>

          <div className="col-12 col-sm-12 col-md-6 col-lg-4">
            <div className="status-card status-card-total">
              <div className="card-header">
                <div className="card-header-left">
                  <h5 className="card-status-title">Total</h5>
                </div>
                <div className="card-header-right">
                </div>
              </div>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 80 }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }}>
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="card-value card-value-total">{cardsData[2].value}</h2>
                  <h5 className="card-percentage card-percentage-total">{cardsData[5].value} do valor total</h5>
                  <hr className="card-divider" />
                  <h5 className="card-assets card-assets-total">{cardsData[8].value} parcelas</h5>
                </>
              )}
            </div>
          </div>
        </div>
        <hr className="section-divider" />
      </div>

      <div className="charts-container">
        <div className="row">
          <div className="col-12 col-lg-8">
            <div className="chart-card">
              <h3 className="chart-title">Aquisição por Fundo</h3>
              <h4 className="chart-subtitle">Milhões de Reais</h4>
              <div className="chart-wrapper">
                {isLoading ? (
                  <div className="chart-empty">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 60 }}>
                      <div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }}>
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  </div>
                ) : aquisicaoPorFundoData.categories.length === 0 ? (
                  <div className="chart-empty">
                    <span>Não há dados para exibir</span>
                  </div>
                ) : (
                  <BarChart data={aquisicaoPorFundoData} config={barChartConfig} />
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="chart-card">
              <h3 className="chart-title">Aquisição por Produto</h3>
              <h4 className="chart-subtitle">Porcentagem</h4>
              <div className="chart-wrapper">
                {isLoading ? (
                  <div className="chart-empty">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 60 }}>
                      <div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }}>
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  </div>
                ) : aquisicaoPorProdutoData.series.length === 0 ? (
                  <div className="chart-empty">
                    <span>Não há dados para exibir</span>
                  </div>
                ) : (
                  <PieChartComponent
                    key={filtroFundo}
                    data={aquisicaoPorProdutoData}
                    config={pieChartConfig}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="table-container">
        <h3 className="table-title">Detalhes por lote</h3>
        <table className="custom-table">
          <thead>
            <tr>
              <th>Nome Remessa</th>
              <th>Fundo</th>
              <th>Produto</th>
              <th>Total de Ativos</th>
              <th>Valor Presente</th>
              <th>Tir da remessa</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="text-center">
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 60 }}>
                    <div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }}>
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : linhaCessoesData?.data?.rows?.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center">Não há dados para exibir</td>
              </tr>
            ) : (
              dadosFiltrados.map((row: any, index: number) => (
                <tr key={row.id || index}>
                  <td>{row["Nome Remessa"]}</td>
                  <td>{row["Fundo"]}</td>
                  <td>{row["Produto"]}</td>
                  <td>{row["Total de Ativos"]}</td>
                  <td>{row["Valor Presente"]?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                  <td>{((row["Tir remessa"] as number) / 100)?.toLocaleString('pt-BR', { style: 'percent', minimumFractionDigits: 2 })}</td>
                </tr>
              )) || []
            )}
          </tbody>
        </table>
      </div>
    </ErrorBoundary>
  );
};

export default CessoesOverview;